import { zodResolver } from '@hookform/resolvers/zod'
import { ApiError, passwordSchema } from '@mevaser/shared'
import { Alert, Button, Field, Input } from '@mevaser/ui'
import { Link, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  CodeForm,
  type CodeChannel,
  type CodeReason,
  completeSignIn,
  SignInShell,
} from '../components/sign-in-shell.js'
import { api } from '../lib/api.js'

const formSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Those do not match',
    path: ['confirmPassword'],
  })

type Values = z.infer<typeof formSchema>

interface ResetResult {
  status: string
  codeSent?: boolean
  codeReason?: CodeReason
  codeChannel?: CodeChannel
  codePhoneLast4?: string
}

/**
 * The other end of the emailed link. A role-holding account still has to
 * clear two-factor authentication afterwards, so this finishes in the same
 * code step the sign-in form uses — the reset is not a way around it.
 */
export function ResetPasswordPage() {
  const { token } = useSearch({ strict: false }) as { token?: string }
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<
    { name: 'password' } | { name: 'code'; email: string | null; result: ResetResult }
  >({ name: 'password' })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(formSchema) })

  const onSubmit = handleSubmit(async (values) => {
    if (!token) return
    setError(null)
    try {
      const result = await api.post<ResetResult>('/auth/password/reset', {
        token,
        newPassword: values.newPassword,
      })
      if (result.status !== 'email_code_required') {
        completeSignIn()
        return
      }
      // The link carried a token, not an identity. The half-authenticated
      // session knows who this is, so ask it rather than showing a code
      // screen that cannot say where the code went.
      const me = await api.get<{ staff: { email: string } | null }>('/auth/me').catch(() => null)
      setStep({ name: 'code', email: me?.staff?.email ?? null, result })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That link is not valid or has expired')
    }
  })

  if (!token) {
    return (
      <SignInShell>
        <h2 className="text-base font-semibold tracking-tight">That link is incomplete</h2>
        <p className="mt-2 text-sm text-foreground-muted">
          It is missing its token — email clients sometimes cut long links in half. Open it again
          from the email, or ask for a new one.
        </p>
        <p className="mt-4 text-center text-xs">
          <Link
            to="/forgot-password"
            className="text-foreground-muted underline-offset-2 hover:underline"
          >
            Send a new link
          </Link>
        </p>
      </SignInShell>
    )
  }

  if (step.name === 'code') {
    return (
      <SignInShell>
        <p className="mb-4 text-sm font-medium">Password set. One more step.</p>
        <CodeForm
          email={step.email}
          codeSent={step.result.codeSent ?? true}
          codeReason={step.result.codeReason}
          channel={step.result.codeChannel}
          phoneLast4={step.result.codePhoneLast4}
        />
      </SignInShell>
    )
  }

  return (
    <SignInShell>
      <h2 className="text-base font-semibold tracking-tight">Set a new password</h2>
      <p className="mt-1 mb-4 text-sm text-foreground-muted">
        At least 12 characters. Length beats punctuation — a phrase you can remember is fine.
      </p>
      {error ? (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="New password" error={errors.newPassword?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('newPassword')}
              type="password"
              autoComplete="new-password"
              autoFocus
              invalid={!!errors.newPassword}
            />
          )}
        </Field>
        <Field label="Confirm password" error={errors.confirmPassword?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('confirmPassword')}
              type="password"
              autoComplete="new-password"
              invalid={!!errors.confirmPassword}
            />
          )}
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={isSubmitting}>
          Set password
        </Button>
      </form>
      <p className="mt-4 text-center text-xs">
        <Link to="/login" className="text-foreground-muted underline-offset-2 hover:underline">
          Back to sign in
        </Link>
      </p>
    </SignInShell>
  )
}
